import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import CustomButton from '../../../components/CustomButton';
import { bookingApiSlice } from '../../../api/booking/bookingApiSlice';
import { CancelSaveMode, useCancelSeriesBookingMutation } from '../../../api/booking/bookingWriteApiSlice';
import { Booking } from './types';

// Removes occurrences from an existing series (#1376 item 1).
//
// Shortening is cancelling occurrences, not deleting them: the per-order cancel
// already exists and keeps the booking on record, which is what an operator
// wants when a series ends early. Only *future* occurrences are offered — a
// past occurrence has already happened, and cancelling one is a different
// intent that belongs on that booking's own page.

interface SeriesShortenPanelProps {
    /** The series these occurrences belong to — the cancel endpoint keys on it. */
    seriesId: string;
    occurrences: Booking[];
    onClose: () => void;
    // `mailFailed` is true only when the operator asked for a customer email
    // and none went out — a requested mail that silently failed must not read
    // as a clean success on this surface either.
    // `outcomeUnknown`: the request did not come back. The server may well
    // have finished — and mailed the customer — so this is reported as unknown
    // rather than as a failure the operator would retry.
    onShortened: (
        canceled: number,
        failed: number,
        mailFailed: boolean,
        outcomeUnknown?: boolean,
    ) => void;
}

const SeriesShortenPanel: React.FC<SeriesShortenPanelProps> = ({
    seriesId,
    occurrences,
    onClose,
    onShortened,
}) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('de') ? 'de' : 'en';
    const dispatch = useDispatch();
    const [cancelSeriesBooking] = useCancelSeriesBookingMutation();

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    // Which of the two commits is running, not just "something is running" —
    // otherwise both buttons spin and the operator cannot tell whether the run
    // in progress is the one with the customer email or the one without.
    const [runningMode, setRunningMode] = useState<CancelSaveMode | null>(null);
    const isCanceling = runningMode !== null;

    // Latest first: shortening a series means dropping dates off the end, so
    // the ones most likely to be ticked sit at the top.
    const cancelableOccurrences = useMemo(() => {
        const now = dayjs();
        return occurrences
            .filter((b) => b.bookingStatus.toLowerCase() !== 'canceled')
            .filter((b) => dayjs(b.startDate).isAfter(now))
            .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
    }, [occurrences]);

    const toggle = (bookingId: string) => {
        setSelectedIds((prev) => (
            prev.includes(bookingId)
                ? prev.filter((id) => id !== bookingId)
                : [...prev, bookingId]
        ));
    };

    // Dropping dates off a running series is news for the customer, so the two
    // commits are offered here the same way the cancel dialog offers them —
    // with and without a customer email. There is no default: the panel cannot
    // know whether this customer has ever been written to.
    const handleConfirm = async (saveMode: CancelSaveMode) => {
        setRunningMode(saveMode);
        let canceled = 0;
        let failed = 0;
        let mailSent = false;
        // Distinct from `canceled`, which counts skipped dates too so the
        // snackbar can say "3 storniert" when the end state matches the intent.
        // Whether a MAIL was due depends on what this request actually
        // cancelled, which is a different number.
        let actuallyCanceled = 0;
        let outcomeUnknown = false;

        try {
            // ONE request, ONE mail listing exactly the dates being dropped.
            // This used to loop the per-booking endpoint, so shortening a
            // series by five dates sent five separate cancellation notices.
            // An occurrence already cancelled elsewhere comes back as
            // `skipped` rather than as the 409 this loop used to swallow.
            const result = await cancelSeriesBooking({
                seriesId,
                saveMode,
                orderIds: selectedIds,
            }).unwrap();
            actuallyCanceled = result?.canceled ?? 0;
            // Skipped dates were already cancelled elsewhere, so the end state
            // matches what the operator asked for — counted as done, the way
            // the old loop counted a 409.
            canceled = actuallyCanceled + (result?.skipped ?? 0);
            // Reported by the server, not inferred: it finishes the run even
            // when an occurrence throws, so "some worked and some did not" is
            // a real outcome with a real number attached.
            // Both are outcomes the operator needs to see: the server could
            // not cancel it, or it is no longer there at all (hard-deleted, or
            // moved out of the series since this list was drawn). Counting
            // only the first would show vanished dates as done.
            failed = (result?.failed ?? 0) + (result?.notFound ?? 0);
            mailSent = result?.mailSent === true;
        } catch {
            // Nothing came back — which is NOT the same as nothing happening.
            // The run is sequential and a long series can outlive the request
            // timeout while the server finishes and mails the customer. So the
            // outcome is reported as unknown and the list is refetched below;
            // claiming "all failed" would send the operator to do it again.
            outcomeUnknown = true;
        }

        // Cancel lives on the new-backend write slice (no tagTypes) — the
        // legacy slice's caches have to be invalidated by hand.
        dispatch(bookingApiSlice.util.invalidateTags(['bookingList', 'bookingDetails']));
        setRunningMode(null);
        // "The email could not be sent" only when one was actually due: if
        // every selected date was already cancelled elsewhere, nothing needed
        // mailing and the absence of a mail is not a failure to report.
        const mailWasDue = saveMode === 'save_and_send_email' && actuallyCanceled > 0;
        onShortened(canceled, failed, mailWasDue && !mailSent, outcomeUnknown);
    };

    return (
        <div className={'mt-4 rounded-lg border border-gray-200 p-4'}>
            <h2 className={'mb-1 text-base font-semibold text-foreground'}>
                {t('series_booking_details.shorten.title')}
            </h2>
            <p className={'mb-4 text-sm text-foreground/60'}>
                {t('series_booking_details.shorten.description')}
            </p>

            {cancelableOccurrences.length === 0 ? (
                <p className={'py-4 text-sm text-foreground/60'}>
                    {t('series_booking_details.shorten.none_cancelable')}
                </p>
            ) : (
                <div className={'max-h-[320px] overflow-y-auto rounded-lg border border-gray-200'}>
                    {cancelableOccurrences.map((booking) => (
                        <label
                            key={booking.bookingId}
                            className={
                                'flex cursor-pointer items-center gap-3 border-b border-gray-100 ' +
                                'px-4 py-3 last:border-b-0'
                            }
                        >
                            <input
                                type={'checkbox'}
                                checked={selectedIds.includes(booking.bookingId)}
                                onChange={() => toggle(booking.bookingId)}
                                disabled={isCanceling}
                                className={'h-4 w-4 rounded border-gray-300 text-primary'}
                            />
                            <span className={'text-sm font-medium'}>
                                {dayjs(booking.startDate).locale(lang).format(
                                    lang === 'de' ? 'dd, DD.MM.YYYY' : 'ddd, MM/DD/YYYY',
                                )}
                            </span>
                            <span className={'text-sm text-foreground/60'}>
                                {dayjs(booking.startDate).format('HH:mm')}
                                &ndash;
                                {dayjs(booking.endDate).format('HH:mm')}
                            </span>
                            <span className={'text-xs text-foreground/40'}>
                                {booking.bookingNumber}
                            </span>
                        </label>
                    ))}
                </div>
            )}

            <div className={'mt-4 flex flex-wrap justify-end gap-2'}>
                <CustomButton
                    title={t('common.cancel')}
                    onClick={onClose}
                    isFilled={false}
                    disabled={isCanceling}
                />
                <CustomButton
                    title={t('series_booking_details.shorten.confirm_without_email', {
                        count: selectedIds.length,
                    })}
                    onClick={() => handleConfirm('save_only')}
                    isFilled={false}
                    disabled={selectedIds.length === 0 || isCanceling}
                    loading={runningMode === 'save_only'}
                />
                <CustomButton
                    title={t('series_booking_details.shorten.confirm', { count: selectedIds.length })}
                    onClick={() => handleConfirm('save_and_send_email')}
                    disabled={selectedIds.length === 0 || isCanceling}
                    loading={runningMode === 'save_and_send_email'}
                />
            </div>
        </div>
    );
};

export default SeriesShortenPanel;
